import { useState, useReducer } from 'react';
import { signInWithPhoneNumber, ConfirmationResult, updateProfile } from 'firebase/auth';
import { auth } from '~/firebase/init';
import * as fp from 'lodash/fp';

import userNameValidation from '~/utils/userNameValidation';
import phoneNumberValidation from '~/utils/phonNumberValidation';

export enum ConfirmStatus {
  NOT_CONFIRM = 'not_confirm',
  CONFIRMING = 'confirming',
  CONFIRMED = 'confirmed',
}

type LoginFormData = {
  name: string;
  phoneNumber: string;
  verifyCode: string;
};

type LoginState = {
  form: LoginFormData;
  error: LoginFormData;
};

enum LoginAction {
  SET_NAME = 'SET_NAME',
  SET_PHONE = 'SET_PHONE',
  SET_VERIFY_CODE = 'SET_VERIFY_CODE',
  SET_ERROR = 'SET_ERROR',
  CLEAR_ERROR = 'CLEAR_ERROR',
  CLEAR_STATE = 'CLEAR_STATE',
  RESET = 'RESET',
}

type LoginActionParams =
  | {
      type: LoginAction.SET_NAME;
      payload: string;
    }
  | {
      type: LoginAction.SET_PHONE;
      payload: string;
    }
  | {
      type: LoginAction.SET_VERIFY_CODE;
      payload: string;
    }
  | {
      type: LoginAction.SET_ERROR;
      payload: Partial<LoginFormData>;
    }
  | {
      type: LoginAction.CLEAR_ERROR;
    }
  | {
      type: LoginAction.CLEAR_STATE;
    }
  | {
      type: LoginAction.RESET;
    };

function reducer(state: LoginState, action: LoginActionParams): LoginState {
  switch (action.type) {
    case LoginAction.SET_NAME: {
      return {
        ...state,
        form: {
          ...state.form,
          name: action.payload,
        },
      };
    }
    case LoginAction.SET_PHONE: {
      return {
        ...state,
        form: {
          ...state.form,
          phoneNumber: action.payload,
        },
      };
    }
    case LoginAction.SET_VERIFY_CODE: {
      return {
        ...state,
        form: {
          ...state.form,
          verifyCode: action.payload,
        },
      };
    }
    case LoginAction.SET_ERROR: {
      return {
        ...state,
        error: {
          ...state.error,
          ...action.payload,
        },
      };
    }
    case LoginAction.CLEAR_STATE: {
      return {
        ...state,
        form: {
          name: '',
          phoneNumber: '',
          verifyCode: '',
        },
      };
    }
    case LoginAction.CLEAR_ERROR: {
      return {
        ...state,
        error: {
          name: '',
          phoneNumber: '',
          verifyCode: '',
        },
      };
    }
    case LoginAction.RESET: {
      return {
        form: {
          name: '',
          phoneNumber: '',
          verifyCode: '',
        },
        error: {
          name: '',
          phoneNumber: '',
          verifyCode: '',
        },
      };
    }
    default: {
      throw new Error('no match action');
    }
  }
}

const defaultState: LoginState = {
  form: {
    name: '',
    phoneNumber: '',
    verifyCode: '',
  },
  error: {
    name: '',
    phoneNumber: '',
    verifyCode: '',
  },
};

export default function useLogin() {
  const [loginState, dispatch] = useReducer(reducer, defaultState);
  const [isVerifying, setVerifying] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(ConfirmStatus.NOT_CONFIRM);
  const [confirmedResult, setConfirmResult] = useState<ConfirmationResult | null>(null);

  const handleReset = () => {
    dispatch({ type: LoginAction.RESET });
    setVerifying(false);
    setConfirmResult(null);
    setConfirmStatus(ConfirmStatus.NOT_CONFIRM);
  };

  const handleSetError = (error: Partial<LoginFormData>) => {
    dispatch({ type: LoginAction.SET_ERROR, payload: error });
  };

  const handleClearState = () => {
    dispatch({ type: LoginAction.CLEAR_STATE });
  };

  const handleClearError = () => {
    dispatch({ type: LoginAction.CLEAR_ERROR });
  };

  const handleChangeName = (value: string) => {
    dispatch({ type: LoginAction.SET_NAME, payload: value });
    handleSetError({ name: '' });
  };

  const handleChangePhoneNumber = (value: string) => {
    dispatch({ type: LoginAction.SET_PHONE, payload: value });
    handleSetError({ phoneNumber: '' });
  };

  const handleChangeVerifyCode = async (value: string) => {
    if (fp.lte(value.length, 6)) {
      dispatch({ type: LoginAction.SET_VERIFY_CODE, payload: value });
      handleSetError({ verifyCode: '' });
    }

    if (fp.eq(value.length, 6) && confirmedResult) {
      try {
        setVerifying(true);
        const result = await confirmedResult.confirm(value);

        const user = result.user;

        await updateProfile(user, {
          displayName: loginState.form.name,
        });
        handleReset();
      } catch (err: any) {
        if (fp.eq(err.code, 'auth/invalid-verification-code')) {
          handleSetError({
            verifyCode: '驗證碼錯誤',
          });
        }
      } finally {
        setVerifying(false);
      }
    }
  };

  const handleLogin = async () => {
    const nameError = userNameValidation(loginState.form.name);

    const { formattedPhoneNumber, error: phoneNumberError } = phoneNumberValidation(
      loginState.form.phoneNumber,
    );

    if (!fp.isEmpty(nameError + phoneNumberError)) {
      handleSetError({
        name: nameError,
        phoneNumber: phoneNumberError,
      });
      return;
    }
    try {
      setConfirmStatus(ConfirmStatus.CONFIRMING);
      await window.recaptchaVerifier.render();

      const confirmationResult = await signInWithPhoneNumber(
        auth,
        formattedPhoneNumber,
        window.recaptchaVerifier,
      );

      setConfirmStatus(ConfirmStatus.CONFIRMED);
      setConfirmResult(confirmationResult);
    } catch (err: any) {
      if (fp.eq(err.code, 'auth/too-many-requests')) {
        handleSetError({ phoneNumber: '登入次數過於頻繁，請稍後再試' });
        setConfirmStatus(ConfirmStatus.NOT_CONFIRM);
        return;
      }

      handleSetError({ phoneNumber: '未知的錯誤，請稍後再試' });
      setConfirmStatus(ConfirmStatus.NOT_CONFIRM);
    }
  };

  return {
    isVerifying,
    confirmStatus,
    state: loginState,
    handleChangeState: {
      changeName: handleChangeName,
      changePhoneNumber: handleChangePhoneNumber,
      changeVerifyCode: handleChangeVerifyCode,
    },
    handleClearError,
    handleClearState,
    handleSetError,
    handleLogin,
  };
}
